import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Paper,
  Card,
  CardContent,
  Divider,
  Stack
} from '@mui/material';
import {
  LocationOn,
  ExpandMore,
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import SelectButtonInput from 'components/form/input/SelectButtonInput'
import { useTranslation } from 'react-i18next';
import { Field } from 'react-final-form';
import SelectInput from 'components/form/input/SelectInput';
import TextInput from 'components/form/input/TextInput';
import ProdutoSelector from 'entity/Produto/components/ProdutoSelector';
import { useFieldValue } from 'components/form/input/NestedField';
import useNotification from 'notification';

import LanguageSelector from 'components/LanguageSelector';
import FormRow from 'components/form/FormRow';
import SeletorMunicipios from 'entity/Municipio/components/SeletorMunicipios';
import RenderQuestoesSecundarias from 'entity/Rodada/components/RodadaForm/Questoes/RenderQuestoesSecundarias';

import termsConditions from 'util/terms-conditions.pdf'
import ImageBox from 'components/basic/ImageBox';
import { Link as MuiLink } from '@mui/material';
import { format } from 'date-fns';
import './styles.css';
import { requiredValidator } from 'components/form/input/validator';
import ImageInput from 'components/form/input/ImageInput';
import TableOtherProducts from '../TableOtherProducts';
import CampoCnpjComBuscador from './CampoCnpjComBuscador';

const formatDate = (dateStr) => format(new Date(dateStr), "dd/MM, HH:mm");

const parseDescription = (text) => {
  // Regex global para links HTTP/HTTPS
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, idx) => {
    if (urlRegex.test(part)) {
      return (
        <MuiLink
          key={idx}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ wordBreak: 'break-word' }}
        >
          {part}
        </MuiLink>
      );
    }
    return <span key={idx}>{part}</span>;
  });
};

export default function InscricaoSimplificadaBox({ rodada, onSubmit, submitFailed, values }) {
  const { t } = useTranslation();
  const { showError } = useNotification();

  // Refs para scroll aos campos com erro
  const tipoPessoaRef = useRef(null);
  const cnpjRef = useRef(null);
  const nomeFantasiaRef = useRef(null);
  const nomeCrachaRef = useRef(null);
  const enderecoRef = useRef(null);
  const municipioRef = useRef(null);
  const descricaoEmpresaRef = useRef(null);
  // const telefoneComercialRef = useRef(null);
  // const celularEmpresaRef = useRef(null);
  const cpfParticipanteRef = useRef(null);
  const nomeParticipanteRef = useRef(null);
  const emailParticipanteRef = useRef(null);
  const celularParticipanteRef = useRef(null);
  const produtosRef = useRef(null);
  const termosRef = useRef(null);

  const [tipoPessoa] = useFieldValue("empresa.tipoPessoa");
  const [produtosVenda] = useFieldValue("empresa.produtosVenda");
  const [produtosCompra] = useFieldValue("empresa.produtosCompra");
  const [checkOutros, setCheckOutros] = useFieldValue("checkOutros");
  const [, setStatus] = useFieldValue("status");
  const [municipio] = useFieldValue("municipio");
  const [termo] = useFieldValue("termo");

  // Função para validar campos obrigatórios e fazer scroll
  const validateAndScrollToFirstError = () => {
    const errors = [];

    // Validação Tipo de Pessoa (apenas se não for internacional)
    if (rodada.rodadaInternacional !== "SIM" && !tipoPessoa) {
      errors.push({
        field: 'tipoPessoa',
        message: t('Tipo de pessoa é obrigatório'),
        ref: tipoPessoaRef
      });
    }

    // Validação CNPJ/CPF (apenas se não for internacional)
    const cnpjValue = values?.empresa?.cnpj;
    if (rodada.rodadaInternacional !== "SIM" && tipoPessoa && !cnpjValue) {
      errors.push({
        field: 'cnpj',
        message: t('CNPJ/CPF é obrigatório'),
        ref: cnpjRef
      });
    }

    // Validação Nome Fantasia
    const nomeFantasiaValue = values?.empresa?.nomeFantasia;
    if (!nomeFantasiaValue || nomeFantasiaValue.trim() === '') {
      errors.push({
        field: 'nomeFantasia',
        message: t('Nome comercial é obrigatório'),
        ref: nomeFantasiaRef
      });
    }

    // Validação Nome no Crachá da Empresa
    const nomeCrachaValue = values?.empresa?.nomeEmpresaCracha;
    if (!nomeCrachaValue || nomeCrachaValue.trim() === '') {
      errors.push({
        field: 'nomeCracha',
        message: t('Nome no crachá da empresa é obrigatório'),
        ref: nomeCrachaRef
      });
    }

    // Validação Endereço
    const enderecoValue = values?.empresa?.endereco;
    if (!enderecoValue || enderecoValue.trim() === '') {
      errors.push({
        field: 'endereco',
        message: t('Endereço é obrigatório'),
        ref: enderecoRef
      });
    }

    // Validação Município
    if (!municipio) {
      errors.push({
        field: 'municipio',
        message: t('Município é obrigatório'),
        ref: municipioRef
      });
    }

    // Validação Descrição da Empresa
    const descricaoEmpresaValue = values?.empresa?.descricaoEmpresa;
    if (!descricaoEmpresaValue || descricaoEmpresaValue.trim() === '') {
      errors.push({
        field: 'descricaoEmpresa',
        message: t('Descrição da empresa é obrigatória'),
        ref: descricaoEmpresaRef
      });
    }

    // const telefoneComercialValue = values?.empresa?.telefoneComercial;
    // if (!telefoneComercialValue || telefoneComercialValue.trim() === '') {
    //   errors.push({
    //     field: 'telComercial',
    //     message: t('Telefone comercial é obrigatório'),
    //     ref: telefoneComercialRef
    //   });
    // }

    // const celularEmpresaValue = values?.empresa?.celular;
    // if (!celularEmpresaValue || celularEmpresaValue.trim() === '') {
    //   errors.push({
    //     field: 'celular',
    //     message: t('Celular da empresa é obrigatório'),
    //     ref: celularEmpresaRef
    //   });
    // }

    // Validação CPF do Participante (apenas se não for internacional)
    const cpfParticipanteValue = values?.empresa?.funcionarios?.cpf;
    if (rodada.rodadaInternacional !== "SIM" && (!cpfParticipanteValue || cpfParticipanteValue.trim() === '')) {
      errors.push({
        field: 'cpfParticipante',
        message: t('CPF do participante é obrigatório'),
        ref: cpfParticipanteRef
      });
    }

    // Validação Nome do Participante
    const nomeParticipanteValue = values?.empresa?.funcionarios?.nomeParticipante;
    if (!nomeParticipanteValue || nomeParticipanteValue.trim() === '') {
      errors.push({
        field: 'nomeParticipante',
        message: t('Nome do participante é obrigatório'),
        ref: nomeParticipanteRef
      });
    }

    // Validação Email do Participante
    const emailParticipanteValue = values?.empresa?.funcionarios?.email;
    if (!emailParticipanteValue || emailParticipanteValue.trim() === '') {
      errors.push({
        field: 'emailParticipante',
        message: t('Email do participante é obrigatório'),
        ref: emailParticipanteRef
      });
    }

    // Validação Celular do Participante
    const celularParticipanteValue = values?.empresa?.funcionarios?.celular;
    if (!celularParticipanteValue || celularParticipanteValue.trim() === '') {
      errors.push({
        field: 'celularParticipante',
        message: t('Celular do participante é obrigatório'),
        ref: celularParticipanteRef
      });
    }

    // Validação Produtos (pelo menos um de venda ou compra)
    if ((!produtosVenda || !produtosVenda.length) && (!produtosCompra || !produtosCompra.length)) {
      errors.push({
        field: 'produtos',
        message: t('É obrigatório informar pelo menos um produto que vende ou compra'),
        ref: produtosRef
      });
    }

    // Validação Termos
    if (!termo) {
      errors.push({
        field: 'termos',
        message: t('Você deve aceitar os termos e condições'),
        ref: termosRef
      });
    }

    // Se há erros, mostra notificação e faz scroll para o primeiro erro
    if (errors.length > 0) {
      const firstError = errors[0];
      showError(firstError.message);

      // Scroll suave para o primeiro campo com erro
      if (firstError.ref?.current) {
        firstError.ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        // Tentar focar no input dentro do ref
        setTimeout(() => {
          const input = firstError.ref.current.querySelector('input, select, textarea');
          if (input) {
            input.focus();
          }
        }, 500);
      }

      return false;
    }

    return true;
  };

  // Função wrapper para onSubmit
  const handleSubmit = () => {
    if (validateAndScrollToFirstError()) {
      onSubmit();
    }
  };

  let produtosRequeridosAlerta = false;
  if (submitFailed && (!produtosVenda && !produtosVenda.length) && (!produtosCompra && !produtosVenda.length)) {
    produtosRequeridosAlerta = true;
  }
  const exibequestoesSecundarias = rodada.questoesSecundarias && rodada.questoesSecundarias.length > 0;
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 2, sm: 3, md: 4 },
        width: '100%',
        alignItems: 'center',
      }}>
        {/* Header da Rodada - Design Moderno */}
        <Card
          className="inscricao-card"
          elevation={6}
          sx={{
            width: '100%',
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
            }
          }}
        >
          <CardContent sx={{
            position: 'relative',
            zIndex: 1,
            p: { xs: 0, md: 6 }
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 3, md: 4 },
              alignItems: { xs: 'center', md: 'center' },
            }}>
              {rodada.capa && (
                <Box sx={{
                  width: { xs: '100%', sm: 320, md: 350 },
                  height: { xs: '100%', sm: 320, md: 350 },
                  borderRadius: { xs: 0, md: 3 },
                  overflow: 'hidden',
                  boxShadow: { xs: '', md: '0 8px 32px rgba(0,0,0,0.3)' },

                  flexShrink: 0,
                }}>
                  <ImageBox
                    src={rodada.capa.base64}
                    alt={`Capa da ${rodada.nome}`}
                    maxWidth="100%"
                    maxHeight="100%"
                    sx={{
                      width: '100%',
                      height: '100%',
                      '& img': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }
                    }}
                  />
                </Box>
              )}

              <Box sx={{
                flex: 1,
                // textAlign: { xs: 'center', md: 'left' },
                p: { xs: 2, md: 0 },
                minWidth: 0,

              }}>
                <Typography
                  variant="h2"
                  fontWeight={700}
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    wordBreak: 'break-word'
                  }}
                >
                  {rodada.nome}
                </Typography>

                <Stack spacing={2} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, }}>
                    <LocationOn sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        fontWeight: 500
                      }}
                    >
                      {rodada.local}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, }}>
                    <CalendarIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    >
                      {`${formatDate(rodada.dataInicio)} até ${formatDate(rodada.dataFinal)}`}
                    </Typography>
                  </Box>
                </Stack>

                {rodada.descricao && (
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                    <DescriptionIcon sx={{ fontSize: 20, mt: 0.5, display: { xs: 'none', sm: 'block' } }} />
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        maxWidth: { xs: '100%', md: '600px' },
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {parseDescription(rodada.descricao)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Formulário de Inscrição - Design Moderno */}
        <Card
          className="inscricao-card"
          elevation={4}
          sx={{
            width: '100%',
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 6 } }}>
            {/* Header do Formulário */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 4,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon sx={{
                  fontSize: { xs: 28, sm: 32 },
                  p: 1,
                  bgcolor: 'primary.main',
                  borderRadius: '50%',
                  color: 'white'
                }} />
                <Typography
                  variant="h4"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                >
                  {t('Inscrição')}
                </Typography>
              </Box>
              <LanguageSelector />
            </Box>

            {/* Seção Empresa */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <BusinessIcon sx={{
                  fontSize: { xs: 24, sm: 28 },
                  p: 1,
                  bgcolor: 'secondary.main',
                  borderRadius: '50%',
                  color: 'white'
                }} />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="secondary.main"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {t('Informações da Empresa')}
                </Typography>
              </Box>

              <Stack spacing={3}>
                <FormRow>
                  <Box ref={tipoPessoaRef} sx={{ flex: 1 }}>
                    <SelectInput
                      sx={{ width: '100%' }}
                      name="empresa.tipoPessoa"
                      label="TIPO DE PESSOA"
                      required={rodada.rodadaInternacional === "SIM" ? false : true}
                    >
                      <MenuItem value={"CNPJ"}>JURÍDICA</MenuItem>
                      <MenuItem value={"CPF"}>FÍSICA</MenuItem>
                    </SelectInput>
                  </Box>
                  {/* <TextInput
                    sx={{ flex: 2 }}
                    name="empresa.cnpj"
                    label={tipoPessoa === "CPF" ? "PESSOA FÍSICA" : "PESSOA JURÍDICA"}
                    mask={!tipoPessoa || rodada.rodadaInternacional === "SIM"
                      ? ""
                      : tipoPessoa === "CPF"
                        ? "000.000.000-00"
                        : "00.000.000/0000-00"
                    }
                    disabled={!tipoPessoa ? true : false}
                    required={rodada.rodadaInternacional === "SIM" ? false : true}
                  /> */}
                  <Box ref={cnpjRef} sx={{ flex: 2 }}>
                    <CampoCnpjComBuscador tipoPessoa={tipoPessoa} rodada={rodada} t={t} />
                  </Box>
                </FormRow>
                <FormRow sx={{ flex: 1 }}>
                  <Box ref={nomeFantasiaRef} sx={{ flex: 1 }}>
                    <TextInput
                      sx={{ width: '100%' }}
                      name="empresa.nomeFantasia"
                      label={t("NOME COMERCIAL")}
                      required
                    />
                  </Box>
                </FormRow>
                <FormRow >
                  <Box ref={nomeCrachaRef} sx={{ flex: 1 }}>
                    <TextInput
                      sx={{ width: '100%' }}
                      name="empresa.nomeEmpresaCracha"
                      label={t("NOME DA EMPRESA (NO CRACHÁ)")}
                      required
                    />
                  </Box>

                  <TextInput name="empresa.razaoSocial" label={t("RAZÃO SOCIAL")} />
                </FormRow>
                <FormRow >
                  <Box ref={enderecoRef} sx={{ flex: 2 }}>
                    <TextInput sx={{ width: '100%' }} name="empresa.endereco" label={t("ENDEREÇO COMPLETO")} />
                  </Box>
                  <Box ref={municipioRef} sx={{ flex: 1 }}>
                    <SeletorMunicipios sx={{ width: '100%' }} name="municipio" label={t("Município")} rodada={rodada} required />
                  </Box>
                </FormRow>
                <FormRow>
                  <TextInput
                    sx={{ flex: 1 }}
                    name="empresa.telComercial"
                    label={t("TELEFONE COMERCIAL")}
                    mask="(00) 0000-0000"
                    required
                  />

                  <TextInput
                    sx={{ flex: 1 }}
                    name="empresa.celular"
                    label={t("CELULAR")}
                    mask="(00) 90000-0000"
                    required
                  />

                  <TextInput sx={{ flex: 2, }} name="empresa.email" label={t("EMAIL")} type="email" required />
                </FormRow>
                <FormRow>
                  <Box ref={descricaoEmpresaRef} sx={{ flex: 2 }}>
                    <TextInput sx={{ width: '100%' }} name="empresa.descricaoEmpresa" label={t("BREVE DESCRIÇÃO DA EMPRESA")}
                      multiline required />
                  </Box>
                </FormRow>
              </Stack>
              {/* <Divider sx={{ my: 3 }} /> */}

              {/* Accordion de Contatos Adicionais */}
              <Accordion sx={{ mt: { xs: 4, sm: 4 } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" fontWeight={600}>
                    {t('Contatos Adicionais')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box >
                    {/* Grid de Redes Sociais - Layout 2x2 em telas grandes */}
                    <FormRow >
                      <TextInput
                        name="empresa.facebook"
                        label={t("FACEBOOK")}
                      />
                      <TextInput
                        name="empresa.instagram"
                        label={t("INSTAGRAM")}
                      />
                    </FormRow>
                    <FormRow >

                      <TextInput
                        name="empresa.linkedin"
                        label={t("LINKEDIN")}
                      />
                      <TextInput
                        name="empresa.youtube"
                        label={t("YOUTUBE")}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInput

                        name="empresa.site"
                        label={t("SITE")}
                      />
                      <TextInput

                        name="empresa.twitter"
                        label={t("TWITTER")}
                      />
                    </FormRow>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* <Divider sx={{ my: 4 }} /> */}

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <PersonIcon sx={{
                  fontSize: { xs: 24, sm: 28 },
                  p: 1,
                  bgcolor: 'info.main',
                  borderRadius: '50%',
                  color: 'white'
                }} />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="info.main"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {t('Dados do Participante')}
                </Typography>
              </Box>
              <Stack spacing={3}>
                <FormRow >
                  <Box ref={cpfParticipanteRef} sx={{ flex: { xs: 1, sm: 0.5 }, minWidth: { xs: '100%', sm: 'auto' } }}>
                    <TextInput
                      sx={{ width: '100%' }}
                      name="empresa.funcionarios.cpf"
                      label="CPF"
                      mask={rodada.rodadaInternacional === "SIM" ? "" : "000.000.000-00"}
                      required={rodada.rodadaInternacional === "SIM" ? false : true}
                    />
                  </Box>
                  <Box ref={nomeParticipanteRef} sx={{ flex: { xs: 1, sm: 2 }, minWidth: { xs: '100%', sm: 'auto' } }}>
                    <TextInput
                      sx={{ width: '100%' }}
                      name="empresa.funcionarios.nomeParticipante"
                      label={t("NOME COMPLETO")}
                      required
                    />
                  </Box>
                </FormRow>
                <FormRow>
                  <TextInput sx={{ flex: 2 }} name="empresa.funcionarios.nomeCracha" label={t("SEU NOME (PARA O CRACHÁ)")} />
                </FormRow>
                <FormRow>
                  <Box ref={emailParticipanteRef} sx={{ flex: 2 }}>
                    <TextInput sx={{ width: '100%' }} name="empresa.funcionarios.email" label={t('Email')} InputLabelProps={{ shrink: true }} required />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }}
                    >
                      {t('Este e-mail será usado para o envio da confirmação da inscrição da rodada.')}
                    </Typography>
                  </Box>
                </FormRow>
                <FormRow >
                  <Box ref={celularParticipanteRef} sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                    <TextInput
                      sx={{ width: '100%' }}
                      name="empresa.funcionarios.celular"
                      label={t("CELULAR")}
                      mask="(00) 90000-0000"
                      required
                    />
                  </Box>
                  <TextInput
                    sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}
                    name="empresa.funcionarios.telComercial"
                    label={t("CONTATO TELEFÔNICO")}
                    mask="(00) 0000-0000"
                  />
                </FormRow>
                <FormRow>
                  <SelectButtonInput
                    name="empresa.funcionarios.sexo"
                    label={t("SEXO")}
                    options={["FEMININO", "MASCULINO", "OUTRO"]}
                    optionsToString={[t("FEMININO"), t("MASCULINO"), t("OUTRO")]}
                  />
                </FormRow>
              </Stack>


            </Box>


            {/* Questões Secundárias */}
            {(exibequestoesSecundarias) && (
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 4 }} />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="warning.main"
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    mb: 3
                  }}
                >
                  {t('Questionário')}
                </Typography>

                <RenderQuestoesSecundarias rodada={rodada} t={t} />
              </Box>
            )}
            <Divider sx={{ my: 4 }} />
            {/* Seção Produtos */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                fontWeight={600}
                color="success.main"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  mb: 3
                }}
              >
                {t('Produtos e Serviços')}
              </Typography>

              {produtosRequeridosAlerta && (
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="body2">
                    {t('É obrigatório informar pelo menos um produto que vende ou compra')}
                  </Typography>
                </Paper>
              )}

              <Box ref={produtosRef}>
                <ProdutoSelector label={t('Produtos/Serviços que VENDE')} name="empresa.produtosVenda" produtosPermitidos={rodada?.produtos} />

                <Divider sx={{ my: 4 }} />

                <ProdutoSelector label={t('Produtos/Serviços que COMPRA')} name="empresa.produtosCompra" produtosPermitidos={rodada?.produtos} />
              </Box>

            </Box>

            <Box sx={{ backgroundColor: "rgba(125,125,125,0.2)", p: 2, width: "100%" }}>
              <div style={{ display: "flex", width: "100%" }}>
                <Field
                  name="checkOutros"
                  component="input"
                  type="checkbox"
                  onChange={(e) => {
                    const { checked } = e.target
                    setCheckOutros(checked)
                    setStatus(checked ? "PENDENTE/INSCRICAO" : "INSCRITO")
                  }}
                />
                <Typography sx={{ ml: 1 }} >
                  {t('Sugerir Outros Produtos/Serviços')}
                </Typography>
              </div>
              {checkOutros && <TableOtherProducts fieldName="outrosProdutos" fieldLabel="Outros Produtos" />}
            </Box>



            {/* Fotos */}
            {rodada.permiteFotosInscricao === "SIM" && (
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 4 }} />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="info.main"
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    mb: 3
                  }}
                >
                  {t('Anexar Fotos')}
                </Typography>

                <FormRow>
                  <ImageInput name={"fotos"} label={'Fotos de Produtos'} maxQt={5} multiple>
                    <FormRow>
                      <TextInput name="descricao" label="Descrição" multiline rows={4} />
                    </FormRow>
                  </ImageInput>
                </FormRow>

              </Box>
            )}

            {/* Termos e Condições */}
            <Box ref={termosRef} sx={{ mb: 4 }}>
              <Divider sx={{ my: 4 }} />
              <Field name={"termo"} validate={requiredValidator}>
                {({ input, meta }) => (
                  <Box>
                    <label>
                      <input
                        type="checkbox"
                        {...input}
                        required
                        style={{ marginRight: 8 }}
                      />                      <Typography variant="body2" component="span">
                        {t('Aceito os')}{' '}
                        <MuiLink
                          href={termsConditions}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('termos e condições')}
                        </MuiLink>
                      </Typography>
                    </label>
                    {meta.error && meta.touched && (
                      <Typography variant="caption" color="error">
                        *Você deve aceitar os termos e condições para prosseguir.
                      </Typography>
                    )}
                  </Box>
                )}
              </Field>
            </Box>

            {/* Botão de Submit */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'uppercase',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(102,126,234,0.4)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {t('Inscrever-se')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
